import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import i18n from "@/i18n.ts";
import { ApiApprovalPolicy } from "@/types/approvals/approval-policy.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiCurrency } from "@/types/currency.types.ts";
import {
  ApiCustomField,
  ApiCustomFieldValue,
  ApiField,
  ApiSection,
} from "@/types/custom-field.types.ts";
import { ApiDepartmentShort } from "@/types/department.types.ts";
import { ApiDivisionShort } from "@/types/division.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { ApiCareerSite } from "@/types/recruit/career-site.types.ts";
import { ApiJobArchivingReason } from "@/types/recruit/job-archiving-reason.types.ts";
import { ApiPipeline } from "@/types/recruit/pipeline.types.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { ApiLocation } from "@/types/location.types.ts";
import { ApiJobProfile } from "@/types/job-profile.types.ts";

export enum JobStatus {
  draft = "draft",
  published = "published",
  on_hold = "on_hold",
  archived = "archived",
}

export enum JobPriority {
  low = "low",
  medium = "medium",
  high = "high",
}

export enum JobSorting {
  touchpoint = "touchpoint",
  priority = "priority",
  title = "title",
}

export interface ApiJobShort {
  datetime_created_at: Date;
  id: number;
  title: string;
  status: JobStatus;
  priority: JobPriority | null;
  description: string | null;
  emoji: string;

  candidates_count: number;
}

export interface ApiJobEmployeeConfig {
  employee_id: number;
  can_view_salary: boolean;
}

export interface ApiJob extends ApiJobShort {
  datetime_created_at: Date;
  department: ApiDepartmentShort | null;
  division: ApiDivisionShort | null;
  stakeholders: ApiEmployee[];
  recruiters: ApiEmployee[];
  pipeline: ApiPipeline;
  job_profile: ApiJobProfile | null;
  location: ApiLocation | null;
  salary_min: number | null;
  salary_max: number | null;
  currency: ApiCurrency | null;
  unread_mention_count: number;
  approval_policy?: ApiApprovalPolicy;
  files: ApiFile[];
  career_sites: ApiCareerSite[];
  job_archiving_reason: ApiJobArchivingReason | null;
  recruiters_config: ApiJobEmployeeConfig[] | null;
  stakeholders_config: ApiJobEmployeeConfig[] | null;
  use_ai: boolean;
  ai_requirements: string | null;
  requirements: string | null;
  responsibilities: string | null;
  conditions: string | null;
  public_title: string | null;
  career_site_apply_config: ApiJobApplyFormSection[] | null;
  custom_fields?: ApiCustomField[];
}

export interface ApiJobApplyFormSection {
  section: ApiSection;
  fields: ApiJobCareerSiteApplyConfig[];
}

export interface ApiJobCareerSiteApplyConfig {
  field: ApiField;
  is_mandatory: boolean;
  is_hidden: boolean;
}

export type JobStatusItem = SelectItem;

export const JobStatusItems = () => {
  return [
    {
      id: JobStatus.draft,
      title: i18n.t("ats:job_status_draft"),
    },
    {
      id: JobStatus.published,
      title: i18n.t("ats:job_status_published"),
    },
    {
      id: JobStatus.on_hold,
      title: i18n.t("ats:job_status_on_hold"),
    },
    {
      id: JobStatus.archived,
      title: i18n.t("ats:job_status_archived"),
    },
  ] as JobStatusItem[];
};

export const isJobActive = (status: JobStatus) => {
  return status === JobStatus.published || status === JobStatus.on_hold;
};

export interface ApiJobSavePayload {
  title: string;
  status: JobStatus;
  priority?: JobPriority | null;
  description?: string | null;
  division_id?: number | null;
  department_id?: number | null;
  salary_min?: number | null;
  salary_max?: number | null;
  currency_code?: string | null;
  job_profile_id?: number | null;
  location_id?: number | null;
  recruiters_config?: ApiJobEmployeeConfig[];
  stakeholders_config?: ApiJobEmployeeConfig[];
  pipeline_id: number;
  emoji: string;
  approval_policy_id?: number | null;
  file_uuids?: string[];
  career_site_ids: number[];
  job_archiving_reason_id: number | null;
  use_ai: boolean;
  ai_requirements: string | null;
  requirements?: string | null;
  responsibilities?: string | null;
  conditions?: string | null;
  public_title?: string | null;
  career_site_apply_config?: ApiApplyFormSectionSave[] | null;
  custom_fields?: Record<string, ApiCustomFieldValue>;
}

export interface ApiApplyFormSectionSave {
  section_id: number;
  fields: ApiApplyFieldConfigSave[];
}

export interface ApiApplyFieldConfigSave {
  field_id: number;
  is_mandatory: boolean;
  is_hidden: boolean;
}

export interface ApiJobsFilter extends ApiFilterBase {
  text?: string;
  status_list: JobStatus[];
  department_ids?: number[];
  recruiter_ids?: number[];
  stakeholder_ids?: number[];
  my_jobs?: boolean;
  sorting?: ApiSorting;
  date_creation_from?: Date;
  date_creation_to?: Date;
}

export const ApiJobsFilterDefault: ApiJobsFilter = {
  page_number: 1,
  status_list: [],
  department_ids: [],
  recruiter_ids: [],
};

export const getJobEmoji = (job: ApiJobShort | undefined) => {
  return job?.emoji || emojiDefaults.jobs.default;
};

export interface ApiJobRequirements {
  ai_requirements: string | null;
}

export interface ApiJobStatusChange {
  datetime_created_at: Date;
  status: JobStatus;
  employee: ApiEmployee;
}

export interface ApiJobHistory {
  datetime_created_at: Date;
  created_by: ApiEmployee | null;
  status_changes: ApiJobStatusChange[];
}

export const getJobPriorityTitle = (priority: JobPriority | null) => {
  switch (priority) {
    case JobPriority.low:
      return i18n.t("ats:job_priority_low");
    case JobPriority.medium:
      return i18n.t("ats:job_priority_medium");
    case JobPriority.high:
      return i18n.t("ats:job_priority_high");
    default:
      return i18n.t("ats:job_priority_not_defined");
  }
};

export const getJobPriorityColorClasses = (
  priority: JobPriority | null
): { dotTextClass: string; stripeBgClass: string } => {
  switch (priority) {
    case JobPriority.low:
      return { dotTextClass: "text-green", stripeBgClass: "bg-green" };
    case JobPriority.medium:
      return { dotTextClass: "text-positive", stripeBgClass: "bg-positive" };
    case JobPriority.high:
      return { dotTextClass: "text-danger", stripeBgClass: "bg-danger" };
    default:
      return { dotTextClass: "text-dark/30", stripeBgClass: "bg-dark/30" };
  }
};
